// Pieces more than one part of the ops catalog draws: the two state chips and the
// OneBill picker the review queue and the product sheet both use. The picker puts
// OneBill products with the product's own code first (#2057), then the closest by name.
// It links a plan, or chooses the plan whose setup Create in OneBill copies (#2156).
import React, { useEffect, useState } from "react";
import { formatCents, type OneBillCandidate } from "@phoneware/lens-domain";
import { Icon } from "../../icons.js";
import { errorText, plural } from "../../format.js";
import { useSession } from "../../session.js";
import { Callout, Chip } from "../../ui.js";
import { useCatalogList } from "./context.js";
import { AT_LOOK, billedText, createDiff, linkLook, pct, perText, planText, scoreClass, type CreateDiff, type Product } from "./model.js";

export function AtChip({ p, testId }: { p: Product; testId?: string }): React.ReactElement {
  const look = AT_LOOK[p.autotaskSync.state];
  return (
    <span className={look.spin ? "chip spin" : look.kind ? `chip ${look.kind}` : "chip"} data-testid={testId} data-status={p.autotaskSync.state}>
      {look.icon ? <Icon name={look.icon} size={10} /> : null}
      {look.label}
    </span>
  );
}

export function LinkChip({ p, testId }: { p: Product; testId?: string }): React.ReactElement {
  const look = linkLook(p);
  return (
    <Chip kind={look.kind} icon={look.icon} testId={testId} status={p.billingLink.state}>
      {look.label}
    </Chip>
  );
}

type Candidates = { status: "loading" } | { status: "ready"; query: string; list: OneBillCandidate[] } | { status: "error"; message: string };

/** Link mode (the default) links the chosen plan; copy mode chooses the plan whose setup a new OneBill product copies. */
type PickerProps = {
  p: Product;
  where: "rq" | "sheet" | "intake";
  busy: boolean;
  products?: Product[];
  onSameCode?: (same: boolean) => void;
} & ({ mode?: "link"; onLink: (productCode: string, pricePlanId: string) => void } | { mode: "copy"; onCopy: (productCode: string, pricePlanId: string) => void });

/**
 * Suggestions from OneBill for one product, best first, and a search by name or
 * code. A OneBill product with this product's code comes first, marked Same code.
 * In copy mode each plan shows its GL code, because Create in OneBill copies the
 * plan's billing, tax, GL and feature setup (#2156). Saving the choice is the
 * caller's, through PUT billing-link. `onSameCode` hears, once the suggestions
 * load, whether OneBill has a product with the code.
 */
export function OneBillPicker(props: PickerProps): React.ReactElement {
  const { p, where, busy, products, onSameCode } = props;
  const copy = props.mode === "copy";
  const { api } = useSession();
  const fromCatalog = useCatalogList();
  const list = products ?? fromCatalog;
  const [query, setQuery] = useState("");
  const [cands, setCands] = useState<Candidates>({ status: "loading" });
  const q = query.trim();

  useEffect(() => {
    let live = true;
    // Typing waits a beat before asking OneBill; the first suggestions load straight away.
    const timer = window.setTimeout(
      () => {
        api.onebillCandidates(p.id, q || undefined).then(
          (found) => {
            if (!live) return;
            setCands({ status: "ready", query: q, list: found });
            if (!q) onSameCode?.(found.some((c) => c.sameCode));
          },
          (err: unknown) => {
            if (live) setCands({ status: "error", message: errorText(err) });
          },
        );
      },
      q ? 250 : 0,
    );
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [api, p.id, q]);

  function choose(productCode: string, pricePlanId: string): void {
    if (props.mode === "copy") props.onCopy(productCode, pricePlanId);
    else props.onLink(productCode, pricePlanId);
  }

  const same = cands.status === "ready" && !cands.query && cands.list.some((c) => c.sameCode);
  const code = p.code.trim();
  return (
    <div className="ob-picker" data-testid={`${where}-picker`} data-mode={copy ? "copy" : "link"}>
      {copy ? (
        <>
          <div className="sect-h">
            <h3 data-testid={`${where}-ob-heading`}>Copy OneBill setup from</h3>
            <span className="note">
              {cands.status === "ready" && cands.query ? `${plural(cands.list.length, "product")} matching \u201c${cands.query}\u201d` : same ? "Same code, then closest by name" : "Closest by name"}
            </span>
          </div>
          <p className="hint first" data-testid={`${where}-copy-rule`}>
            Omni creates {p.name} in OneBill with the billing, tax, GL and feature setup of the plan you choose. Its code, name, description and price come from Omni:{" "}
            {formatCents(p.listPrice)}
            {perText(p.billing)}.
          </p>
        </>
      ) : (
        <>
          <div className="sect-h">
            <h3 data-testid={`${where}-ob-heading`}>{q ? `OneBill products matching \u201c${q}\u201d` : same ? "Same code in OneBill, then closest by name" : "Closest in OneBill by name"}</h3>
            {cands.status === "ready" && cands.query ? <span className="note">{cands.list.length} shown</span> : null}
          </div>
          {cands.status === "ready" && !cands.query && !same ? (
            <p className="hint pc-nocode" data-testid={`${where}-ob-nocode`}>
              <Icon name="info" size={12} /> {code ? `OneBill has no product with code ${code}.` : "This product has no code, so Omni can only suggest by name."}
            </p>
          ) : null}
        </>
      )}
      <label className="search">
        <Icon name="search" size={15} />
        <span className="sr">Search OneBill</span>
        <input className="input" type="search" placeholder="Search OneBill by name or code" data-testid={`${where}-ob-search`} value={query} onChange={(e) => setQuery(e.target.value)} />
      </label>
      {cands.status === "loading" ? (
        <p className="empty">Looking in OneBill.</p>
      ) : cands.status === "error" ? (
        <Callout kind="stop" icon="triangle-alert">
          {cands.message}
        </Callout>
      ) : cands.list.length === 0 ? (
        <p className="empty" data-testid={`${where}-ob-empty`}>
          {cands.query
            ? `No OneBill product matches \u201c${cands.query}\u201d. ${copy ? "Try fewer words." : "Try fewer words, or choose Create in OneBill."}`
            : copy
              ? "No OneBill product shares a word with this name. Search by name or code."
              : "No OneBill product shares a word with this name. Search by code, or choose Create in OneBill."}
        </p>
      ) : (
        <div className="ob-cands" data-testid={`${where}-cands`}>
          {cands.list.map((c) => {
            const used = copy ? undefined : list.find((x) => x.id !== p.id && x.onebill?.productCode === c.productCode);
            return (
              <div key={c.productCode} className={c.sameCode ? "cand pc-same" : "cand"} data-testid={`onebill-candidate-${c.productCode}`} data-same-code={c.sameCode ? "true" : undefined}>
                <div className="cand-h">
                  <b>{c.name}</b>
                  <span className="code">{c.productCode}</span>
                  {c.sameCode ? (
                    <span className="chip ok pc-samechip" data-testid={`onebill-same-code-${c.productCode}`}>
                      <Icon name="check" size={10} />
                      Same code
                    </span>
                  ) : (
                    <span className={scoreClass(c.score)}>{c.score > 0 ? `${pct(c.score)} name match` : "Search result"}</span>
                  )}
                </div>
                {used ? (
                  <p className="hint">
                    <Icon name="info" size={12} /> Already billing {used.name}. Link only if both are the same thing.
                  </p>
                ) : null}
                {c.pricePlans.length === 0 ? <p className="hint">This product has no price plans in OneBill.</p> : null}
                {c.pricePlans.map((pl) => (
                  <div key={pl.id} className="plan">
                    <span>
                      {pl.name}
                      {copy ? (
                        <span className="plan-meta">
                          <span className="code">Plan {pl.id}</span>
                          <span className={pl.glCode ? "gl" : "gl none"} data-testid={`gl-${c.productCode}-${pl.id}`}>
                            {pl.glCode ? `GL ${pl.glCode}` : "No GL code"}
                          </span>
                        </span>
                      ) : (
                        <span className="code block">Plan {pl.id}</span>
                      )}
                    </span>
                    <span className="amount">{planText(pl)}</span>
                    <span>
                      {!p.billing ? (
                        <span className="off">{billedText(p)}</span>
                      ) : pl.billing !== p.billing ? (
                        <span className="off">{p.billing === "monthly" ? "This plan doesn\u2019t bill monthly" : "This plan doesn\u2019t bill once"}</span>
                      ) : copy ? (
                        <button className="btn small" type="button" disabled={busy} data-testid={`onebill-copy-${c.productCode}-${pl.id}`} onClick={() => choose(c.productCode, pl.id)}>
                          <Icon name="copy" size={14} />
                          Copy this setup
                        </button>
                      ) : (
                        <button className="btn small" type="button" disabled={busy} data-testid={`onebill-link-${c.productCode}-${pl.id}`} onClick={() => choose(c.productCode, pl.id)}>
                          <Icon name="link" size={14} />
                          Link this plan
                        </button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** A dollar amount box: "$" inside, cents out through parseDollars. */
export function PriceBox({ id, label, value, placeholder, onChange }: { id: string; label: string; value: string; placeholder?: string; onChange: (v: string) => void }): React.ReactElement {
  return (
    <span className="pricebox">
      <span className="cur">$</span>
      <input className="input" id={id} inputMode="decimal" aria-label={label} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
    </span>
  );
}

/** Each field of a created OneBill product that differs from the one it copied (#2156). */
export function CreateDiffTable({ diff, testId }: { diff: CreateDiff; testId?: string }): React.ReactElement {
  return (
    <table className="diff" data-testid={testId}>
      <thead>
        <tr>
          <th>Field</th>
          <th>Expected</th>
          <th>OneBill has</th>
        </tr>
      </thead>
      <tbody>
        {diff.rows.map((r) => (
          <tr key={r.field}>
            <td>{r.field}</td>
            <td>{r.expected}</td>
            <td>{r.got}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** A create that failed, inside a sync row: OneBill's words in red, then the fields when its product differs from the one it copied. */
export function CreateFailure({ message, lead = "", testId }: { message: string; lead?: string; testId: string }): React.ReactElement {
  const diff = createDiff(message);
  return (
    <span className="ob-fail" data-testid={testId}>
      <span className="g-bad">
        {lead}
        {diff ? `${diff.head}. ${diff.tail}` : message}
      </span>
      {diff ? <CreateDiffTable diff={diff} testId={`${testId}-diff`} /> : null}
    </span>
  );
}
